import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "@xstyled/styled-components";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { API_URL } from "../../../config";
import { tokens } from "../../../utils/theme";
import {
  FaTelegramPlane,
  FaDiscord,
  FaPlus,
  FaMinus,
  FaSearch,
  FaCopy,
  FaArrowUp,
  FaArrowDown,
  FaCheck,
  FaBookmark,
  FaTrash,
  FaGripVertical
} from "react-icons/fa";
import {
  FiRefreshCw,
  FiKey,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiInfo,
  FiExternalLink,
  FiEdit2,
  FiBookmark
} from "react-icons/fi";

// --- STYLED COMPONENTS ---

const Workspace = styled.div`
  display: flex;
  height: calc(100vh - 200px);
  min-height: 580px;
  background: ${props => props.theme.palette.mode === 'dark' ? '#0f111a' : '#f8fafc'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 14px;
  overflow: hidden;
  margin: 12px 20px 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  position: relative;

  @keyframes spinRefresh {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .spinning,
  &.spinning svg,
  svg.spinning {
    animation: spinRefresh 0.8s linear infinite !important;
    display: inline-block;
  }
`;

const CollapseToggleBtn = styled.button`
  position: absolute;
  top: 18px;
  left: ${props => props.$isOpen ? '296px' : '12px'};
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid ${props => props.$isOpen
    ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)')
    : '#82aaff'};
  background: ${props => props.$isOpen
    ? (props.theme.palette.mode === 'dark' ? 'rgba(22, 25, 37, 0.95)' : '#ffffff')
    : (props.theme.palette.mode === 'dark' ? 'linear-gradient(135deg, #1e293b, #0f172a)' : 'linear-gradient(135deg, #2563eb, #3b82f6)')};
  color: ${props => props.$isOpen
    ? props.theme.palette.text.primary
    : '#ffffff'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  cursor: pointer;
  z-index: 60;
  box-shadow: ${props => props.$isOpen ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 4px 14px rgba(37, 99, 235, 0.4)'};
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              background 0.2s ease,
              border-color 0.2s ease,
              color 0.2s ease,
              transform 0.15s ease;

  &:hover {
    transform: scale(1.08);
    border-color: #82aaff;
  }
`;

// --- LEFT PANE: CHANNELS LIST ---
const ChannelsSidebar = styled.div`
  width: ${props => props.$isOpen ? '340px' : '0px'};
  min-width: ${props => props.$isOpen ? '290px' : '0px'};
  opacity: ${props => props.$isOpen ? '1' : '0'};
  overflow: hidden;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  border-right: ${props => props.$isOpen
    ? (props.theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)')
    : 'none'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(22, 25, 37, 0.95)' : '#ffffff'};
  display: flex;
  flex-direction: column;
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              min-width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.2s ease;
  white-space: nowrap;
`;

const SidebarHeader = styled.div`
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'};
`;


const HeaderNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)'};
  padding: 3px;
  border-radius: 10px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.07)'};
`;

const HeaderNavTab = styled.button`
  height: 32px;
  border: 1px solid ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.35)' : 'rgba(37, 99, 235, 0.25)')
    : 'transparent'};
  background: ${props => props.$active
    ? (props.theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(130, 170, 255, 0.16), rgba(130, 170, 255, 0.05))'
        : '#ffffff')
    : 'transparent'};
  color: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb')
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)')};
  padding: 0 14px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: ${props => props.$active ? '0 2px 8px rgba(0, 0, 0, 0.12)' : 'none'};
  transition: all 0.15s ease;

  &:hover {
    color: ${props => props.theme.palette.mode === 'dark' ? '#ffffff' : '#000000'};
  }
`;

const SavedCountBadge = styled.span`
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 800;
  background: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.25)' : 'rgba(37, 99, 235, 0.15)')
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)')};
  color: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb')
    : props.theme.palette.text.primary};
`;

const BookmarkIconBtn = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.$isSaved ? '#82aaff' : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    color: #82aaff;
    background: rgba(130, 170, 255, 0.15);
    transform: scale(1.12);
  }
`;

const ChannelJumpPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.25)' : 'rgba(37, 99, 235, 0.2)'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.08)' : 'rgba(37, 99, 235, 0.05)'};
  color: ${props => props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb'};
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.18)' : 'rgba(37, 99, 235, 0.12)'};
    transform: translateY(-1px);
  }
`;

const ContextMenuBox = styled.div`
  position: fixed;
  z-index: 9999;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(22, 25, 37, 0.98)' : '#ffffff'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.3)' : 'rgba(0, 0, 0, 0.12)'};
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 190px;
  animation: fadeInCtx 0.12s ease-out;

  @keyframes fadeInCtx {
    from { opacity: 0; transform: scale(0.96); }
    to { opacity: 1; transform: scale(1); }
  }
`;

const ContextMenuItem = styled.button`
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${props => props.theme.palette.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.15)' : 'rgba(37, 99, 235, 0.08)'};
    color: ${props => props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb'};
  }
`;

const ToastBox = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(22, 25, 37, 0.96)' : '#ffffff'};
  color: ${props => props.theme.palette.text.primary};
  border: 1px solid rgba(130, 170, 255, 0.4);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(8px);
  padding: 10px 22px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
  pointer-events: none;
  animation: slideUpToast 0.2s ease-out;

  @keyframes slideUpToast {
    from { opacity: 0; transform: translate(-50%, 15px); }
    to { opacity: 1; transform: translate(-50%, 0); }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IconBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${props => props.$primary
    ? '#82aaff'
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)')};
  background: ${props => props.$primary
    ? 'linear-gradient(135deg, #2563eb, #3b82f6)'
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)')};
  color: ${props => props.$primary ? '#ffffff' : (props.$danger ? '#ef4444' : props.theme.palette.text.primary)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: ${props => props.$danger
      ? 'rgba(239, 68, 68, 0.2)'
      : (props.$primary ? 'linear-gradient(135deg, #1d4ed8, #2563eb)' : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'))};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.spinning svg,
  svg.spinning {
    animation: spinRefresh 0.8s linear infinite !important;
    display: inline-block;
  }

  @keyframes spinRefresh {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SidebarControls = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'};
`;

const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 10px;
    font-size: 13px;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 7px 10px 7px 32px;
  border-radius: 8px;
  font-size: 12px;
  outline: none;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(15, 17, 26, 0.6)' : '#ffffff'};
  color: ${props => props.theme.palette.text.primary};

  &:focus {
    border-color: #82aaff;
  }
`;

const PlatformFilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PlatformFilterPill = styled.button`
  flex: 1;
  height: 31px;
  padding: 0 8px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.4)' : '#2563eb')
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)')};
  background: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.15)' : 'rgba(37, 99, 235, 0.1)')
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)')};
  color: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb')
    : props.theme.palette.text.secondary};

  &:hover {
    background: ${props => props.$active
      ? (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.22)' : 'rgba(37, 99, 235, 0.15)')
      : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.06)')};
  }
`;

const ChannelList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  gap: 6px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(130, 170, 255, 0.3);
    border-radius: 2px;
  }
`;

const ChannelItem = styled.div`
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  border-radius: 10px;
  border: 1px solid ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.8)' : '#2563eb')
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)')};
  background: ${props => props.$active
    ? (props.theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(130, 170, 255, 0.18), rgba(130, 170, 255, 0.06))'
        : 'linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(37, 99, 235, 0.04))')
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)')};
  box-shadow: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? '0 4px 14px rgba(130, 170, 255, 0.18)' : '0 4px 12px rgba(37, 99, 235, 0.12)')
    : 'none'};
  transition: transform 0.15s ease, border-color 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease;
  position: relative;

  ${props => props.$isDragging && `
    opacity: 0.35;
    transform: scale(0.98);
  `}

  ${props => props.$dragOverPosition === 'before' && `
    border-top: 2px solid #82aaff !important;
    transform: translateY(2px);
  `}

  ${props => props.$dragOverPosition === 'after' && `
    border-bottom: 2px solid #82aaff !important;
    transform: translateY(-2px);
  `}

  &:hover {
    border-color: ${props => props.$active
      ? (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.9)' : '#2563eb')
      : (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.35)' : 'rgba(37, 99, 235, 0.3)')};
    background: ${props => props.$active
      ? (props.theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(130, 170, 255, 0.22), rgba(130, 170, 255, 0.08))'
          : 'linear-gradient(135deg, rgba(37, 99, 235, 0.15), rgba(37, 99, 235, 0.06))')
      : (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.06)' : 'rgba(37, 99, 235, 0.04)')};
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.25)'};
  cursor: grab;
  padding: 3px 2px;
  border-radius: 4px;
  transition: all 0.15s ease;
  font-size: 11px;
  flex-shrink: 0;

  &:hover {
    color: ${props => props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb'};
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.12)' : 'rgba(37, 99, 235, 0.08)'};
  }

  &:active {
    cursor: grabbing;
  }
`;

const ChannelLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const ChannelDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb')
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)')};
  box-shadow: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? '0 0 8px #82aaff' : '0 0 8px #2563eb')
    : 'none'};
  flex-shrink: 0;
  transition: all 0.2s ease;
`;

const ChannelNameText = styled.span`
  font-size: 13px;
  font-weight: ${props => props.$active ? '700' : '600'};
  color: ${props => props.$active
    ? (props.theme.palette.mode === 'dark' ? '#ffffff' : '#1e293b')
    : props.theme.palette.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.15s ease;
`;

const ChannelActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const ChannelActionBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.4;
  transition: all 0.15s ease;
  font-size: 13px;

  ${ChannelItem}:hover & {
    opacity: 0.9;
  }

  &:hover {
    color: #82aaff;
    background: rgba(130, 170, 255, 0.18);
    opacity: 1;
    transform: scale(1.08);
  }
`;

const DeleteChannelBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.4;
  transition: all 0.15s ease;

  ${ChannelItem}:hover & {
    opacity: 0.9;
  }

  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.15);
    opacity: 1;
  }
`;

// --- RIGHT PANE: DISCUSSION FEED ---
const DiscussionMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.palette.mode === 'dark' ? '#0f111a' : '#ffffff'};
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const PaneContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  ${props => !props.$active && `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
    z-index: -1;
  `}
`;

const DiscussionHeader = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(22, 25, 37, 0.8)' : 'rgba(255, 255, 255, 0.9)'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderChannelMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChannelHeaderBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 18px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.28)' : 'rgba(37, 99, 235, 0.22)'};
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(22, 25, 37, 0.95), rgba(15, 17, 26, 0.9))'
    : 'linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.9))'};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(8px);
`;

const ChannelLiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.7);
  animation: pulseLive 2s infinite ease-in-out;
  flex-shrink: 0;

  @keyframes pulseLive {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.25);
    }
  }
`;

const ChannelTitleText = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: ${props => props.theme.palette.text.primary};
`;

const LiveStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 12px;
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
`;

const MessageList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-width: thin;
  scrollbar-color: #82aaff rgba(255, 255, 255, 0.05);

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.04)'};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #82aaff;
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #507aff;
  }
`;

const MessageCard = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(22, 25, 37, 0.7)' : '#f8fafc'};
  border: 1px solid ${props => props.$hasSignal
    ? (props.$signalDirection === 'LONG' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)')
    : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)')};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: ${props => props.$hasSignal ? '0 4px 15px rgba(0, 0, 0, 0.15)' : 'none'};
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const AuthorBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #82aaff, #2563eb);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
`;

const AuthorName = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: ${props => props.theme.palette.text.primary};
`;

const MessageTime = styled.span`
  font-size: 11px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const MessageText = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: ${props => props.theme.palette.text.primary};
  margin: 0;
  white-space: pre-wrap;
`;

// Trade Signal Detected Box
const SignalBox = styled.div`
  background: ${props => props.$direction === 'LONG'
    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(5, 150, 105, 0.05))'
    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(220, 38, 38, 0.05))'};
  border: 1px solid ${props => props.$direction === 'LONG' ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.35)'};
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const SignalLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SignalBadge = styled.span`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  color: #ffffff;
  background: ${props => props.$direction === 'LONG' ? '#10b981' : '#ef4444'};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const SignalLevels = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  font-family: monospace;
`;

const LevelSpan = styled.span`
  color: ${props => props.$color || props.theme.palette.text.primary};
  font-weight: 700;
`;

// Media Components (Charts, Photos, Videos)
const MediaContainer = styled.div`
  margin: 10px 0;
  border-radius: 10px;
  overflow: hidden;
  max-width: 520px;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.04)'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
  display: flex;
  justify-content: flex-start;
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 380px;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;
  display: block;
  transition: transform 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: scale(1.015);
    filter: brightness(1.05);
  }
`;

const VideoPlayer = styled.video`
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  outline: none;
  background: #000;
`;

// --- DAY DIVIDERS & DAY NAVIGATION ---
const DayDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0 10px;
  position: relative;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
    z-index: 1;
  }
`;

const DayBadge = styled.div`
  position: relative;
  z-index: 2;
  padding: 4px 14px;
  border-radius: 20px;
  background: ${props => props.theme.palette.mode === 'dark' ? '#181b2a' : '#f1f5f9'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.25)' : 'rgba(130, 170, 255, 0.35)'};
  color: #82aaff;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const DayNavToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 20px;
  padding: 2px 6px;
`;

const DayNavArrowBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${props => props.theme.palette.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s ease;

  &:hover {
    background: #82aaff;
    color: #fff;
    transform: scale(1.1);
  }
`;

const FloatingDayNav = styled.div`
  position: absolute;
  right: 22px;
  bottom: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(22, 25, 37, 0.92)' : 'rgba(255, 255, 255, 0.92)'};
  backdrop-filter: blur(8px);
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  border-radius: 30px;
  padding: 6px 4px;
  z-index: 100;
`;

const FloatingNavBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9'};
  color: ${props => props.theme.palette.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;

  &:hover {
    background: #82aaff;
    color: #ffffff;
    transform: scale(1.12);
  }
`;

// --- MODALS ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? '#181b27' : '#ffffff'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.15)'};
  border-radius: 16px;
  width: ${props => props.$width || '480px'};
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: ${props => props.theme.palette.text.primary};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 800;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const FormInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.15)'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(15, 17, 26, 0.95)' : '#ffffff'};
  color: ${props => props.theme.palette.text.primary};
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.45)' : 'rgba(37, 99, 235, 0.45)'};
  }

  &:focus {
    border-color: ${props => props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb'};
    box-shadow: 0 0 0 3px ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.18)' : 'rgba(37, 99, 235, 0.18)'};
  }

  &::placeholder {
    color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)'};
  }
`;

const FormSelect = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 38px 0 14px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.15)'};
  background-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(15, 17, 26, 0.95)' : '#ffffff'};
  color: ${props => props.theme.palette.text.primary};
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: ${props => props.theme.palette.mode === 'dark' 
    ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2382aaff' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`
    : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%232563eb' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`};
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 13px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.5)' : 'rgba(37, 99, 235, 0.5)'};
    background-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(22, 26, 40, 0.95)' : '#ffffff'};
  }

  &:focus {
    border-color: ${props => props.theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb'};
    box-shadow: 0 0 0 3px ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.18)' : 'rgba(37, 99, 235, 0.18)'};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.04)'};
    border-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'};
    color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)'};
    background-image: ${props => props.theme.palette.mode === 'dark' 
      ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.2)' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`
      : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='rgba(0,0,0,0.2)' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`};
  }

  option {
    background-color: ${props => props.theme.palette.mode === 'dark' ? '#181b27' : '#ffffff'};
    color: ${props => props.theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b'};
    padding: 10px 14px;
    font-size: 13px;
  }
`;

const RefreshSpinBtn = styled.button`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(88, 101, 242, 0.15)' : 'rgba(88, 101, 242, 0.1)'};
  border: 1px solid rgba(88, 101, 242, 0.35);
  color: #82aaff;
  border-radius: 8px;
  padding: 0 13px;
  height: 40px;
  cursor: pointer;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    background: rgba(88, 101, 242, 0.25);
    border-color: #5865F2;
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.spinning svg {
    animation: spinRefresh 0.7s linear infinite;
  }

  @keyframes spinRefresh {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
`;

const SubmitBtn = styled.button`
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);

  &:hover {
    opacity: 0.92;
  }
`;

const DangerBtn = styled.button`
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.35);
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(239, 68, 68, 0.45);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// --- COMPONENT LOGIC ---

export default function IdeasTab() {
  const theme = useTheme();
  const messagesEndRef = useRef(null);

  // Channels state
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [platformFilter, setPlatformFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Messages state
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [refreshingChannel, setRefreshingChannel] = useState(false);
  const [msgSearch, setMsgSearch] = useState("");
  const filterSignalsOnly = false;
  const sortOrder = "chronological";
  const messageListRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const channelScrollPosRef = useRef(0);
  const prevChannelIdRef = useRef(null);
  const [, setActiveDayLabel] = useState("End");


  // Modals state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);

  // Discord Guilds & Channels state
  const [discordGuilds, setDiscordGuilds] = useState([]);
  const [selectedGuild, setSelectedGuild] = useState("");
  const [guildChannels, setGuildChannels] = useState([]);
  const [loadingGuildChannels, setLoadingGuildChannels] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedDiscordChannel, setSelectedDiscordChannel] = useState("");
  const [refreshingGuilds, setRefreshingGuilds] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [channelDetailsModal, setChannelDetailsModal] = useState(null);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const [deletingChannel, setDeletingChannel] = useState(false);
  const [isEditingChannel, setIsEditingChannel] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    category: "Fundamentals",
    url: "",
    handle: "",
    description: ""
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Channel Drag & Drop state
  const [draggedChannelId, setDraggedChannelId] = useState(null);
  const [dragOverChannelId, setDragOverChannelId] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null); // 'before' | 'after'

  // Saved Messages & Bookmarks state
  const [sidebarTab, setSidebarTab] = useState("channels"); // "channels" | "saved"
  const [savedMessages, setSavedMessages] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedSearch, setSavedSearch] = useState("");
  const [contextMenu, setContextMenu] = useState(null); // { x, y, message }
  const [saveToast, setSaveToast] = useState(null);

  // Config info
  const [configStatus, setConfigStatus] = useState({
    telegram_connected: false,
    discord_connected: false
  });
  const [configForm, setConfigForm] = useState({
    telegram_api_id: "",
    telegram_api_hash: "",
    telegram_session_string: "",
    telegram_bot_token: "",
    discord_token: ""
  });

  // Channel Form
  const [channelForm, setChannelForm] = useState({
    platform: "telegram",
    name: "",
    author: "",
    server: "",
    discord_category: "",
    handle: "",
    channel_id: "",
    category: "Fundamentals",
    description: "",
    url: ""
  });

  // 1. Load Channels from Backend
  const fetchChannels = async () => {
    try {
      const res = await axios.get(`${API_URL}/social/channels`);
      setChannels(res.data);
      if (res.data.length > 0 && !selectedChannelId) {
        setSelectedChannelId(res.data[0].id);
      }
    } catch (e) {
      console.error("Failed to load channels:", e);
    }
  };

  // 2. Load Config from Backend
  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/social/config`);
      setConfigStatus(res.data);
    } catch (e) {
      console.error("Failed to load social config:", e);
    }
  };

  // 3. Load Saved Messages from Backend
  const fetchSavedMessages = async () => {
    setLoadingSaved(true);
    const startTime = Date.now();
    try {
      const res = await axios.get(`${API_URL}/social/saved-messages`);
      setSavedMessages(res.data || []);
    } catch (e) {
      console.error("Failed to load saved messages:", e);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 750 - elapsed);
      setTimeout(() => {
        setLoadingSaved(false);
      }, remaining);
    }
  };

  useEffect(() => {
    fetchChannels();
    fetchConfig();
    fetchSavedMessages();
    fetchDiscordGuilds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDiscordGuilds = async () => {
    setRefreshingGuilds(true);
    try {
      const res = await axios.get(`${API_URL}/social/discord/guilds`);
      const gList = res.data || [];
      setDiscordGuilds(gList);
      if (gList.length > 0 && !selectedGuild) {
        const firstGid = gList[0].id;
        setSelectedGuild(firstGid);
        fetchGuildChannels(firstGid);
      }
    } catch (e) {
      console.error("Failed to load discord guilds:", e);
    } finally {
      setTimeout(() => setRefreshingGuilds(false), 500);
    }
  };

  const fetchGuildChannels = async (gid) => {
    if (!gid) {
      setGuildChannels([]);
      setSelectedCategory("");
      setSelectedDiscordChannel("");
      return;
    }
    setLoadingGuildChannels(true);
    setSelectedCategory("");
    setSelectedDiscordChannel("");
    try {
      const res = await axios.get(`${API_URL}/social/discord/guilds/${gid}/channels`);
      setGuildChannels(res.data || []);
    } catch (e) {
      console.error("Failed to load guild channels:", e);
    } finally {
      setLoadingGuildChannels(false);
    }
  };

  // Close context menu on outside click
  useEffect(() => {
    const handleOutsideClick = () => setContextMenu(null);
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  const showToast = (msg) => {
    setSaveToast(msg);
    setTimeout(() => setSaveToast(null), 2400);
  };

  const isMessageSaved = (msgId) => {
    return savedMessages.some(m => String(m.id) === String(msgId));
  };

  const toggleSaveMessage = async (msg) => {
    if (!msg) return;
    const idStr = String(msg.id);
    const already = isMessageSaved(idStr);

    if (already) {
      try {
        await axios.delete(`${API_URL}/social/saved-messages/${idStr}`);
        setSavedMessages(prev => prev.filter(m => String(m.id) !== idStr));
        showToast("Removed from Saved");
      } catch (err) {
        console.error("Failed to remove saved message:", err);
      }
    } else {
      const payload = {
        id: idStr,
        channel_id: selectedChannel?.id || msg.channel_id || "",
        channel_name: selectedChannel?.name || msg.channel_name || "Channel",
        channel_platform: selectedChannel?.platform || msg.platform || "telegram",
        author: msg.author || selectedChannel?.name || "Trader",
        date: msg.date || msg.timestamp || new Date().toISOString(),
        text: msg.text || "",
        media_info: msg.media_info || null,
        signal: msg.signal || null,
        saved_at: new Date().toISOString()
      };

      try {
        await axios.post(`${API_URL}/social/saved-messages`, payload);
        setSavedMessages(prev => [payload, ...prev.filter(m => String(m.id) !== idStr)]);
        showToast("Saved to Bookmarks!");
      } catch (err) {
        console.error("Failed to save message:", err);
      }
    }
  };

  // 3. Fetch Messages when active channel changes or on manual refresh
  const fetchMessages = async (cid, isManualRefresh = false) => {
    if (!cid) return;
    if (isManualRefresh) {
      setRefreshingChannel(true);
    } else {
      setLoadingMessages(true);
    }
    const startTime = Date.now();
    try {
      const res = await axios.get(`${API_URL}/social/channels/${cid}/messages?limit=40`);
      setMessages(res.data.messages || []);
    } catch (e) {
      console.error("Failed to load channel messages:", e);
    } finally {
      if (isManualRefresh) {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 750 - elapsed);
        setTimeout(() => {
          setRefreshingChannel(false);
        }, remaining);
      } else {
        setLoadingMessages(false);
      }
    }
  };

  useEffect(() => {
    if (selectedChannelId) {
      fetchMessages(selectedChannelId);
    }
  }, [selectedChannelId]);

  // Selected Channel Object
  const selectedChannel = useMemo(() => {
    return channels.find(c => c.id === selectedChannelId) || channels[0] || null;
  }, [channels, selectedChannelId]);

  // Filtered Channels for sidebar
  const filteredChannels = useMemo(() => {
    return channels.filter(c => {
      if (platformFilter !== "ALL" && c.platform !== platformFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = c.name?.toLowerCase().includes(q);
        const matchHandle = c.handle?.toLowerCase().includes(q);
        if (!matchName && !matchHandle) return false;
      }
      return true;
    });
  }, [channels, platformFilter, searchQuery]);

  // Filtered Saved Messages
  const filteredSavedMessages = useMemo(() => {
    if (!savedSearch.trim()) return savedMessages;
    const q = savedSearch.toLowerCase();
    return savedMessages.filter(m => {
      const matchText = m.text?.toLowerCase().includes(q);
      const matchAuthor = m.author?.toLowerCase().includes(q);
      const matchChannel = m.channel_name?.toLowerCase().includes(q);
      const matchSignal = m.signal && (
        m.signal.pair?.toLowerCase().includes(q) ||
        m.signal.asset?.toLowerCase().includes(q) ||
        m.signal.direction?.toLowerCase().includes(q)
      );
      return matchText || matchAuthor || matchChannel || matchSignal;
    });
  }, [savedMessages, savedSearch]);

  // Filtered and Sorted Messages in discussion (Newest first by default)
  const filteredMessages = useMemo(() => {
    const list = messages.filter(m => {
      if (filterSignalsOnly && !m.signal) return false;
      if (msgSearch.trim()) {
        const q = msgSearch.toLowerCase();
        const matchText = m.text?.toLowerCase().includes(q);
        const matchAuthor = m.author?.toLowerCase().includes(q);
        if (!matchText && !matchAuthor) return false;
      }
      return true;
    });

    return [...list].sort((a, b) => {
      // 1. Try date / timestamp comparison
      const strA = a.date || a.timestamp || "";
      const strB = b.date || b.timestamp || "";
      const timeA = strA ? new Date(strA).getTime() : 0;
      const timeB = strB ? new Date(strB).getTime() : 0;

      if (!isNaN(timeA) && !isNaN(timeB) && timeA !== 0 && timeB !== 0) {
        if (timeA !== timeB) {
          return sortOrder === "newest" ? timeB - timeA : timeA - timeB;
        }
      }

      // 2. Fallback to Telegram numeric message ID (higher ID = more recent)
      const idA = parseInt(a.id, 10);
      const idB = parseInt(b.id, 10);
      if (!isNaN(idA) && !isNaN(idB) && idA !== idB) {
        return sortOrder === "newest" ? idB - idA : idA - idB;
      }

      return 0;
    });
  }, [messages, filterSignalsOnly, msgSearch, sortOrder]);

  // Helper to extract day key YYYY-MM-DD
  const getMsgDayKey = (msg) => {
    if (!msg) return "";
    const dStr = msg.date || msg.timestamp || "";
    if (!dStr) return "";
    const m = dStr.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    try {
      const d = new Date(dStr);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    } catch (e) {}
    return "";
  };

  // Helper to format friendly day badge
  // Helper to format friendly day badge
  const formatDayBadge = (dateKey) => {
    if (!dateKey) return "Unknown Date";
    try {
      const parts = dateKey.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const targetDate = new Date(year, month, day);

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const diffDays = Math.round((today - targetDate) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "Yesterday";
        if (diffDays > 1 && diffDays <= 7) {
          return `${diffDays} days ago (${targetDate.toLocaleDateString("en-US", { weekday: 'short', month: 'short', day: 'numeric' })})`;
        }
        return targetDate.toLocaleDateString("en-US", { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch (e) {}
    return dateKey;
  };

  // Chronological list of distinct days present in current messages (earliest first, latest last)
  const distinctDaysChronological = useMemo(() => {
    const set = new Set();
    filteredMessages.forEach(m => {
      const k = getMsgDayKey(m);
      if (k) set.add(k);
    });
    return Array.from(set).sort();
  }, [filteredMessages]);

  // Helper to get exact scroll position of a day header inside messageList container
  const getDayTargetScroll = (container, dayKey) => {
    const el = document.getElementById(`day-header-${dayKey}`);
    if (!el) return null;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    return Math.max(0, container.scrollTop + (elRect.top - containerRect.top) - 10);
  };


  // Auto-scroll to bottom of discussion when messages are loaded or channel changes
  useEffect(() => {
    if (!loadingMessages && messages.length > 0 && messageListRef.current) {
      const container = messageListRef.current;
      const doScrollToBottom = () => {
        if (container) {
          container.scrollTop = container.scrollHeight;
          isAtBottomRef.current = true;
          setActiveDayLabel("End");
        }
      };
      doScrollToBottom();
      const t1 = setTimeout(doScrollToBottom, 60);
      const t2 = setTimeout(doScrollToBottom, 250);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [loadingMessages, messages, selectedChannelId]);

  // Sync scrollbar position with activeDayLabel and remember scroll position
  const handleScroll = () => {
    if (!messageListRef.current) return;
    const container = messageListRef.current;
    channelScrollPosRef.current = container.scrollTop;
    const currentScroll = container.scrollTop;
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);

    if (maxScroll - currentScroll <= 60) {
      isAtBottomRef.current = true;
      setActiveDayLabel("End");
      return;
    }

    isAtBottomRef.current = false;
    const days = distinctDaysChronological;
    if (days.length === 0) return;

    if (currentScroll <= 30) {
      setActiveDayLabel("Start");
      return;
    }

    const containerTop = container.getBoundingClientRect().top;
    let closestDay = null;
    let minDistance = Infinity;

    days.forEach((d) => {
      const el = document.getElementById(`day-header-${d}`);
      if (el) {
        const rect = el.getBoundingClientRect();
        const dist = Math.abs(rect.top - containerTop);
        if (rect.top <= containerTop + 140 && dist < minDistance) {
          minDistance = dist;
          closestDay = d;
        }
      }
    });

    if (closestDay) {
      setActiveDayLabel(formatDayBadge(closestDay));
    }
  };

  // Reset channel scroll position when switching to a different channel
  useEffect(() => {
    if (prevChannelIdRef.current && prevChannelIdRef.current !== selectedChannelId) {
      channelScrollPosRef.current = 0;
    }
    prevChannelIdRef.current = selectedChannelId;
  }, [selectedChannelId]);

  // Restore channel scroll position when switching back from 'saved' to 'channels'
  useEffect(() => {
    if (sidebarTab === 'channels' && messageListRef.current) {
      const savedPos = channelScrollPosRef.current;
      requestAnimationFrame(() => {
        if (messageListRef.current && savedPos > 0) {
          messageListRef.current.scrollTop = savedPos;
        }
      });
      setTimeout(() => {
        if (messageListRef.current && savedPos > 0) {
          messageListRef.current.scrollTop = savedPos;
        }
      }, 40);
    }
  }, [sidebarTab]);

  // Channel Drag & Drop handlers
  const handleDragStart = (e, cid) => {
    setDraggedChannelId(cid);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', cid);
  };

  const handleDragOver = (e, cid) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (cid === draggedChannelId) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const pos = e.clientY < midY ? 'before' : 'after';

    if (dragOverChannelId !== cid || dragOverPosition !== pos) {
      setDragOverChannelId(cid);
      setDragOverPosition(pos);
    }
  };

  const handleDragEnd = () => {
    setDraggedChannelId(null);
    setDragOverChannelId(null);
    setDragOverPosition(null);
  };

  const handleDrop = async (e, targetCid) => {
    e.preventDefault();
    if (!draggedChannelId || draggedChannelId === targetCid) {
      handleDragEnd();
      return;
    }

    const currentChannels = [...channels];
    const fromIndex = currentChannels.findIndex(c => c.id === draggedChannelId);
    const toIndex = currentChannels.findIndex(c => c.id === targetCid);

    if (fromIndex === -1 || toIndex === -1) {
      handleDragEnd();
      return;
    }

    const [moved] = currentChannels.splice(fromIndex, 1);
    const newTargetIndex = currentChannels.findIndex(c => c.id === targetCid);
    const insertIndex = dragOverPosition === 'after' ? newTargetIndex + 1 : newTargetIndex;
    currentChannels.splice(insertIndex, 0, moved);

    setChannels(currentChannels);
    handleDragEnd();

    try {
      const channelIds = currentChannels.map(c => c.id);
      await axios.put(`${API_URL}/social/channels/reorder`, { channel_ids: channelIds });
      showToast("Channel order updated");
    } catch (err) {
      console.error("Failed to persist channel order:", err);
    }
  };

  // Day Navigation Handlers:
  // Arrow Up (▲):
  // 1st click from bottom of discussion -> jump up to start of Today
  // 2nd click -> jump up to start of Yesterday (Day -1)
  // 3rd click -> jump up to start of Day -2, etc.
  const handleNavUp = () => {
    if (!messageListRef.current) return;
    const container = messageListRef.current;
    const days = distinctDaysChronological;
    if (days.length === 0) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const currentScroll = container.scrollTop;
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    const isAtBottom = (maxScroll - currentScroll) <= 60 || isAtBottomRef.current;

    // If at bottom of discussion: jump directly to start of Today (latest day)
    if (isAtBottom) {
      const todayKey = days[days.length - 1];
      const targetScroll = getDayTargetScroll(container, todayKey);
      if (targetScroll !== null) {
        container.scrollTo({ top: targetScroll, behavior: 'smooth' });
        setActiveDayLabel(formatDayBadge(todayKey));
        isAtBottomRef.current = false;
        return;
      }
    }

    // Otherwise, find the day target immediately above current position
    let prevTargetScroll = null;
    let prevTargetDay = null;
    for (let i = days.length - 1; i >= 0; i--) {
      const st = getDayTargetScroll(container, days[i]);
      if (st !== null && st < currentScroll - 35) {
        prevTargetScroll = st;
        prevTargetDay = days[i];
        break;
      }
    }

    if (prevTargetScroll !== null && prevTargetDay) {
      container.scrollTo({ top: prevTargetScroll, behavior: 'smooth' });
      setActiveDayLabel(formatDayBadge(prevTargetDay));
      isAtBottomRef.current = false;
    } else {
      // Reached earliest day or top: scroll to top
      container.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveDayLabel("Start");
      isAtBottomRef.current = false;
    }
  };

  // Arrow Down (▼):
  // Descend from Day -2 -> start of Yesterday -> start of Today -> End of discussion
  const handleNavDown = () => {
    if (!messageListRef.current) return;
    const container = messageListRef.current;
    const days = distinctDaysChronological;
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);

    if (days.length === 0) {
      container.scrollTo({ top: maxScroll, behavior: 'smooth' });
      return;
    }

    const currentScroll = container.scrollTop;

    // Find the first day target below current position
    let nextTargetScroll = null;
    let nextTargetDay = null;
    for (let i = 0; i < days.length; i++) {
      const st = getDayTargetScroll(container, days[i]);
      if (st !== null && st > currentScroll + 35) {
        nextTargetScroll = st;
        nextTargetDay = days[i];
        break;
      }
    }

    if (nextTargetScroll !== null && nextTargetDay) {
      container.scrollTo({ top: nextTargetScroll, behavior: 'smooth' });
      setActiveDayLabel(formatDayBadge(nextTargetDay));
      isAtBottomRef.current = false;
    } else {
      // Reached or passed latest day (Today) -> scroll to end of discussion
      container.scrollTo({ top: maxScroll, behavior: 'smooth' });
      setActiveDayLabel("End");
      isAtBottomRef.current = true;
    }
  };

  // Add Channel handler
  const handleAddChannel = async (e) => {
    e.preventDefault();
    if (!channelForm.name.trim()) return;

    if (channelForm.platform === "discord" && !channelForm.channel_id && !channelForm.handle) {
      alert("Please select a Discord channel from the dropdown first.");
      return;
    }

    try {
      const rawHandle = (channelForm.handle || channelForm.channel_id || "").trim();
      let cleanTarget = rawHandle;
      if (channelForm.platform === "discord") {
        cleanTarget = channelForm.channel_id || rawHandle;
      } else {
        cleanTarget = rawHandle.replace(/^(?:https?:\/\/)?(?:t\.me\/|@)+/i, '').replace(/\/+$/, '');
      }

      const payload = {
        name: channelForm.name.trim(),
        author: channelForm.author?.trim() || channelForm.name.trim(),
        server: channelForm.server || (channelForm.platform === "discord" ? (channelForm.author || "Discord") : ""),
        discord_category: channelForm.discord_category || "",
        platform: channelForm.platform,
        handle: channelForm.platform === "telegram" ? `@${cleanTarget}` : rawHandle,
        channel_id: cleanTarget,
        category: channelForm.category,
        description: channelForm.description,
        url: channelForm.url || (channelForm.platform === "telegram" ? `https://t.me/${cleanTarget}` : (cleanTarget.match(/^\d+$/) ? `https://discord.com/channels/${cleanTarget}` : rawHandle)),
        avatar: channelForm.platform === "telegram" ? "✈️" : "🎮"
      };

      const res = await axios.post(`${API_URL}/social/channels`, payload);
      setAddModalOpen(false);
      await fetchChannels();
      if (res.data?.channel?.id) {
        setSelectedChannelId(res.data.channel.id);
      }
      setChannelForm({
        platform: "telegram",
        name: "",
        author: "",
        server: "",
        discord_category: "",
        handle: "",
        channel_id: "",
        category: "Fundamentals",
        description: "",
        url: ""
      });
    } catch (e) {
      alert("Failed to add channel: " + e.message);
    }
  };

  // Delete Channel handler with in-app confirmation
  const confirmDeleteChannel = async () => {
    if (!channelToDelete) return;
    setDeletingChannel(true);
    try {
      await axios.delete(`${API_URL}/social/channels/${channelToDelete.id}`);
      const remaining = channels.filter(c => c.id !== channelToDelete.id);
      setChannels(remaining);
      if (selectedChannelId === channelToDelete.id && remaining.length > 0) {
        setSelectedChannelId(remaining[0].id);
      }
      showToast(`Removed "${channelToDelete.name}" from channels`);
      setChannelToDelete(null);
    } catch (e) {
      showToast("Failed to delete channel: " + e.message);
    } finally {
      setDeletingChannel(false);
    }
  };

  // Edit Channel handlers
  const handleStartEdit = () => {
    if (!channelDetailsModal) return;
    setEditForm({
      name: channelDetailsModal.name || "",
      category: channelDetailsModal.category || "Fundamentals",
      url: channelDetailsModal.url || "",
      handle: channelDetailsModal.handle || "",
      description: channelDetailsModal.description || ""
    });
    setIsEditingChannel(true);
  };

  const handleSaveEdit = async (e) => {
    if (e) e.preventDefault();
    if (!channelDetailsModal || !editForm.name.trim()) return;
    setSavingEdit(true);
    try {
      const payload = {
        ...channelDetailsModal,
        name: editForm.name.trim(),
        category: editForm.category,
        description: editForm.description?.trim() || ""
      };

      const res = await axios.put(`${API_URL}/social/channels/${channelDetailsModal.id}`, payload);
      const updated = res.data?.channel || payload;
      setChannels(prev => prev.map(c => c.id === updated.id ? updated : c));
      setChannelDetailsModal(updated);
      setIsEditingChannel(false);
    } catch (e) {
      alert("Failed to update channel: " + e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // Save Social Config
  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/social/config`, configForm);
      setConfigModalOpen(false);
      await fetchConfig();
      if (selectedChannelId) {
        fetchMessages(selectedChannelId);
      }
    } catch (e) {
      alert("Failed to save credentials: " + e.message);
    }
  };


  // Copy Trade Signal
  const handleCopySignal = (sig) => {
    const text = `🎯 [${sig.direction}] ${sig.asset}\n📍 Entry: ${sig.entry || 'Market'} | SL: ${sig.stop_loss || '-'} | TP: ${sig.take_profit || '-'}`;
    navigator.clipboard.writeText(text);
    alert("Trade call copied to clipboard!");
  };

  return (
    <Workspace theme={theme}>
      {/* Dynamic Inverting Collapse/Expand Button (< -> >) */}
      <CollapseToggleBtn
        theme={theme}
        $isOpen={sidebarOpen}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <FiChevronLeft /> : <FiChevronRight />}
      </CollapseToggleBtn>

      {/* 1. LEFT PANE: FOLLOWED CHANNELS & SAVED TRADES */}
      <ChannelsSidebar theme={theme} $isOpen={sidebarOpen}>
        <SidebarHeader theme={theme}>
          <HeaderNavGroup theme={theme}>
            <HeaderNavTab
              type="button"
              theme={theme}
              $active={sidebarTab === 'channels'}
              onClick={() => setSidebarTab('channels')}
            >
              Channels
            </HeaderNavTab>
            <HeaderNavTab
              type="button"
              theme={theme}
              $active={sidebarTab === 'saved'}
              onClick={() => setSidebarTab('saved')}
            >
              <FiBookmark style={{ fontSize: '11px' }} />
              Saved
              {savedMessages.length > 0 && (
                <SavedCountBadge theme={theme} $active={sidebarTab === 'saved'}>
                  {savedMessages.length}
                </SavedCountBadge>
              )}
            </HeaderNavTab>
          </HeaderNavGroup>

          <HeaderActions style={{ marginRight: '34px' }}>
            {sidebarTab === 'channels' && (
              <IconBtn
                theme={theme}
                $primary
                onClick={() => {
                  fetchDiscordGuilds();
                  setAddModalOpen(true);
                }}
                title="Add Channel"
              >
                <FaPlus />
              </IconBtn>
            )}
          </HeaderActions>
        </SidebarHeader>

        {sidebarTab === 'channels' ? (
          <>
            <SidebarControls theme={theme}>
              <SearchBox theme={theme}>
                <FaSearch />
                <SearchInput
                  theme={theme}
                  placeholder="Search channels..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </SearchBox>
              <PlatformFilterBar theme={theme}>
                <PlatformFilterPill
                  theme={theme}
                  $active={platformFilter === "ALL"}
                  onClick={() => setPlatformFilter("ALL")}
                  title="All channels"
                >
                  All ({channels.length})
                </PlatformFilterPill>
                <PlatformFilterPill
                  theme={theme}
                  $active={platformFilter === "telegram"}
                  onClick={() => setPlatformFilter("telegram")}
                  title="Telegram"
                >
                  <FaTelegramPlane style={{ color: '#229ED9', fontSize: '12px' }} /> ({channels.filter(c => c.platform === 'telegram').length})
                </PlatformFilterPill>
                <PlatformFilterPill
                  theme={theme}
                  $active={platformFilter === "discord"}
                  onClick={() => setPlatformFilter("discord")}
                  title="Discord"
                >
                  <FaDiscord style={{ color: '#5865F2', fontSize: '12px' }} /> ({channels.filter(c => c.platform === 'discord').length})
                </PlatformFilterPill>
              </PlatformFilterBar>
            </SidebarControls>

            {/* Channel Items List */}
            <ChannelList>
              {filteredChannels.map(ch => {
                const isSelected = ch.id === selectedChannelId;
                return (
                  <ChannelItem
                    key={ch.id}
                    theme={theme}
                    $active={isSelected}
                    $platform={ch.platform}
                    $isDragging={draggedChannelId === ch.id}
                    $dragOverPosition={dragOverChannelId === ch.id ? dragOverPosition : null}
                    draggable={!searchQuery.trim()}
                    onDragStart={(e) => handleDragStart(e, ch.id)}
                    onDragOver={(e) => handleDragOver(e, ch.id)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => handleDrop(e, ch.id)}
                    onClick={() => setSelectedChannelId(ch.id)}
                  >
                    <ChannelLeft>
                      <DragHandle
                        theme={theme}
                        title="Drag to reorder channel"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaGripVertical />
                      </DragHandle>
                      {ch.platform === "discord" ? (
                        <FaDiscord style={{ color: '#5865F2', fontSize: '13px', flexShrink: 0 }} />
                      ) : (
                        <FaTelegramPlane style={{ color: '#229ED9', fontSize: '13px', flexShrink: 0 }} />
                      )}
                      <ChannelDot theme={theme} $active={isSelected} />
                      <ChannelNameText theme={theme} $active={isSelected}>
                        {ch.name}
                      </ChannelNameText>
                    </ChannelLeft>

                    {/* Actions: Info Details & Delete (-) */}
                    <ChannelActionsGroup onClick={(e) => e.stopPropagation()}>
                      <ChannelActionBtn
                        theme={theme}
                        onClick={(e) => {
                          e.stopPropagation();
                          setChannelDetailsModal(ch);
                        }}
                      >
                        <FiInfo />
                      </ChannelActionBtn>
                      <DeleteChannelBtn
                        theme={theme}
                        onClick={(e) => {
                          e.stopPropagation();
                          setChannelToDelete(ch);
                        }}
                        title="Delete channel"
                      >
                        <FaMinus />
                      </DeleteChannelBtn>
                    </ChannelActionsGroup>
                  </ChannelItem>
                );
              })}
            </ChannelList>
          </>
        ) : (
          <>
            <SidebarControls theme={theme}>
              <SearchBox theme={theme}>
                <FaSearch />
                <SearchInput
                  theme={theme}
                  placeholder="Search saved..."
                  value={savedSearch}
                  onChange={(e) => setSavedSearch(e.target.value)}
                />
              </SearchBox>
            </SidebarControls>

            {/* Saved Items Compact List */}
            <ChannelList>
              {filteredSavedMessages.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', opacity: 0.6, fontSize: '12px' }}>
                  No saved messages yet
                </div>
              ) : (
                filteredSavedMessages.map((sm, idx) => (
                  <ChannelItem
                    key={sm.id || idx}
                    theme={theme}
                    $active={false}
                    onClick={() => {
                      const el = document.getElementById(`saved-msg-${sm.id}`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#82aaff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sm.channel_name || "Channel"}
                        </span>
                        <span style={{ fontSize: '10px', opacity: 0.5 }}>{sm.date ? sm.date.slice(0, 10) : ""}</span>
                      </div>
                      <span style={{ fontSize: '11.5px', opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sm.signal ? `⚡ ${sm.signal.direction} ${sm.signal.asset || sm.signal.pair}` : (sm.text || "Media")}
                      </span>
                    </div>
                  </ChannelItem>
                ))
              )}
            </ChannelList>
          </>
        )}
      </ChannelsSidebar>

      {/* 2. RIGHT PANE: CHANNEL DISCUSSION FEED OR SAVED TRADES */}
      <DiscussionMain theme={theme}>
        {/* PANE A: SAVED TRADES */}
        <PaneContainer $active={sidebarTab === 'saved'}>
            {/* Discussion Header for Saved Trades */}
            <DiscussionHeader theme={theme}>
              <HeaderChannelMeta style={{ marginLeft: sidebarOpen ? '0px' : '48px', transition: 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <ChannelHeaderBadge theme={theme}>
                  <FiBookmark style={{ color: '#82aaff' }} />
                  <ChannelTitleText theme={theme}>Saved Trades</ChannelTitleText>
                  <SavedCountBadge theme={theme} $active={true}>
                    {filteredSavedMessages.length}
                  </SavedCountBadge>
                </ChannelHeaderBadge>
              </HeaderChannelMeta>

              <HeaderActions>
                <SearchBox theme={theme} style={{ width: '180px' }}>
                  <FaSearch />
                  <SearchInput
                    theme={theme}
                    placeholder="Search saved..."
                    value={savedSearch}
                    onChange={(e) => setSavedSearch(e.target.value)}
                  />
                </SearchBox>
                <IconBtn
                  theme={theme}
                  onClick={fetchSavedMessages}
                  title="Refresh"
                  disabled={loadingSaved}
                  className={loadingSaved ? "spinning" : ""}
                >
                  <FiRefreshCw
                    className={loadingSaved ? "spinning" : ""}
                    style={{
                      animation: loadingSaved ? 'spinRefresh 0.8s linear infinite' : 'none',
                      display: 'inline-block'
                    }}
                  />
                </IconBtn>
              </HeaderActions>
            </DiscussionHeader>

            {/* Saved Messages Feed */}
            <MessageList>
              {loadingSaved ? (
                <div style={{ textAlign: 'center', padding: '40px', opacity: 0.6 }}>
                  <FiRefreshCw
                    className="spinning"
                    style={{ fontSize: '24px', animation: 'spinRefresh 0.8s linear infinite', display: 'inline-block' }}
                  />
                  <p>Loading saved messages...</p>
                </div>
              ) : filteredSavedMessages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '80px 20px', opacity: 0.6 }}>
                  <FiBookmark style={{ fontSize: '36px', color: '#82aaff', marginBottom: '12px' }} />
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 800 }}>No saved messages yet</h4>
                  <p style={{ maxWidth: '420px', margin: '0 auto', fontSize: '13px', lineHeight: '1.5' }}>
                    When scrolling in any channel, right-click on any message or trade call and click <strong>"Save Message"</strong> to collect it here.
                  </p>
                </div>
              ) : (
                filteredSavedMessages.map((sm, idx) => (
                  <MessageCard
                    key={sm.id || idx}
                    id={`saved-msg-${sm.id}`}
                    theme={theme}
                    $hasSignal={Boolean(sm.signal)}
                    $signalDirection={sm.signal?.direction}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({ x: e.clientX, y: e.clientY, message: sm });
                    }}
                  >
                    <MessageHeader>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <ChannelJumpPill
                          type="button"
                          theme={theme}
                          onClick={() => {
                            if (sm.channel_id) {
                              setSelectedChannelId(sm.channel_id);
                              setSidebarTab('channels');
                            }
                          }}
                          title="Jump to channel"
                        >
                          <span>{sm.channel_name || "Channel"}</span>
                          <FiExternalLink style={{ fontSize: '11px' }} />
                        </ChannelJumpPill>
                        <AuthorBlock>
                          <AuthorAvatar>
                            {sm.author?.charAt(0)?.toUpperCase() || "A"}
                          </AuthorAvatar>
                          <AuthorName theme={theme}>{sm.author}</AuthorName>
                        </AuthorBlock>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MessageTime theme={theme}>{sm.date}</MessageTime>
                        <BookmarkIconBtn
                          type="button"
                          theme={theme}
                          $isSaved={true}
                          onClick={() => toggleSaveMessage(sm)}
                          title="Remove from Saved"
                        >
                          <FaBookmark />
                        </BookmarkIconBtn>
                      </div>
                    </MessageHeader>

                    {sm.text && <MessageText theme={theme}>{sm.text}</MessageText>}

                    {sm.media_info && (
                      <MediaContainer theme={theme}>
                        {sm.media_info.type === 'image' && sm.media_info.url && (
                          <ImagePreview
                            src={sm.media_info.url.startsWith('http') ? sm.media_info.url : `${API_URL}${sm.media_info.url}`}
                            alt="Saved chart"
                            onClick={() => setLightboxImage(sm.media_info.url.startsWith('http') ? sm.media_info.url : `${API_URL}${sm.media_info.url}`)}
                          />
                        )}
                      </MediaContainer>
                    )}

                    {sm.signal && (
                      <SignalBox $direction={sm.signal.direction}>
                        <SignalLeft>
                          <SignalBadge $direction={sm.signal.direction}>
                            {sm.signal.direction === 'LONG' ? <FaArrowUp /> : <FaArrowDown />}
                            {sm.signal.direction}
                          </SignalBadge>
                          <strong style={{ fontSize: '13px' }}>{sm.signal.asset || sm.signal.pair}</strong>
                        </SignalLeft>

                        <SignalLevels>
                          {sm.signal.entry && (
                            <span>Entry: <LevelSpan theme={theme}>{sm.signal.entry}</LevelSpan></span>
                          )}
                          {sm.signal.stop_loss && (
                            <span>SL: <LevelSpan theme={theme} $color="#ef4444">{sm.signal.stop_loss}</LevelSpan></span>
                          )}
                          {sm.signal.take_profit && (
                            <span>TP: <LevelSpan theme={theme} $color="#10b981">{sm.signal.take_profit}</LevelSpan></span>
                          )}

                          <IconBtn
                            theme={theme}
                            style={{ width: '26px', height: '26px', fontSize: '11px' }}
                            onClick={() => handleCopySignal(sm.signal)}
                            title="Copy trade signal"
                          >
                            <FaCopy />
                          </IconBtn>
                        </SignalLevels>
                      </SignalBox>
                    )}
                  </MessageCard>
                ))
              )}
            </MessageList>
        </PaneContainer>

        {/* PANE B: CHANNEL DISCUSSION FEED */}
        <PaneContainer $active={sidebarTab === 'channels'}>
          {selectedChannel ? (
            <>
            {/* Discussion Header */}
            <DiscussionHeader theme={theme}>
              <HeaderChannelMeta style={{ marginLeft: sidebarOpen ? '0px' : '48px', transition: 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <ChannelHeaderBadge theme={theme}>
                  <ChannelLiveDot />
                  {selectedChannel.platform === 'discord' ? (
                    <FaDiscord style={{ color: '#5865F2', fontSize: '16px' }} />
                  ) : (
                    <FaTelegramPlane style={{ color: '#229ED9', fontSize: '16px' }} />
                  )}
                  <ChannelTitleText theme={theme}>{selectedChannel.name}</ChannelTitleText>
                </ChannelHeaderBadge>
              </HeaderChannelMeta>

              <HeaderActions>
                <SearchBox theme={theme} style={{ width: '160px' }}>
                  <FaSearch />
                  <SearchInput
                    theme={theme}
                    placeholder="Search chat..."
                    value={msgSearch}
                    onChange={(e) => setMsgSearch(e.target.value)}
                  />
                </SearchBox>

                {/* Day Navigation Controls in Toolbar */}
                <DayNavToolbar theme={theme}>
                  <DayNavArrowBtn
                    type="button"
                    theme={theme}
                    onClick={handleNavUp}
                  >
                    <FaArrowUp />
                  </DayNavArrowBtn>
                  <DayNavArrowBtn
                    type="button"
                    theme={theme}
                    onClick={handleNavDown}
                  >
                    <FaArrowDown />
                  </DayNavArrowBtn>
                </DayNavToolbar>

                <IconBtn
                  theme={theme}
                  onClick={() => fetchMessages(selectedChannel.id, true)}
                  title="Refresh"
                  disabled={loadingMessages || refreshingChannel}
                  className={(loadingMessages || refreshingChannel) ? "spinning" : ""}
                >
                  <FiRefreshCw
                    className={(loadingMessages || refreshingChannel) ? "spinning" : ""}
                    style={{
                      animation: (loadingMessages || refreshingChannel) ? 'spinRefresh 0.8s linear infinite' : 'none',
                      display: 'inline-block'
                    }}
                  />
                </IconBtn>
              </HeaderActions>
            </DiscussionHeader>

            {/* Messages Feed */}
            <MessageList ref={messageListRef} onScroll={handleScroll}>
              {loadingMessages ? (
                <div style={{ textAlign: 'center', padding: '40px', opacity: 0.6 }}>
                  <FiRefreshCw
                    className="spinning"
                    style={{ fontSize: '24px', animation: 'spinRefresh 0.8s linear infinite', display: 'inline-block' }}
                  />
                  <p>Fetching messages from {selectedChannel.name}...</p>
                </div>
              ) : filteredMessages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', opacity: 0.6 }}>
                  <p>No messages match your current filters.</p>
                </div>
              ) : (
                filteredMessages.map((msg, idx) => {
                  const dayKey = getMsgDayKey(msg);
                  const prevDayKey = idx > 0 ? getMsgDayKey(filteredMessages[idx - 1]) : null;
                  const isNewDay = dayKey && dayKey !== prevDayKey;
                  const hasSignal = Boolean(msg.signal);
                  return (
                    <React.Fragment key={msg.id || idx}>
                      {isNewDay && (
                        <DayDivider
                          data-day-header="true"
                          id={`day-header-${dayKey}`}
                          theme={theme}
                        >
                          <DayBadge theme={theme}>
                            <FiCalendar /> {formatDayBadge(dayKey)}
                          </DayBadge>
                        </DayDivider>
                      )}

                      <MessageCard
                        id={`msg-${msg.id || idx}`}
                        theme={theme}
                        $hasSignal={hasSignal}
                        $signalDirection={msg.signal?.direction}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setContextMenu({ x: e.clientX, y: e.clientY, message: msg });
                        }}
                      >
                      <MessageHeader>
                        <AuthorBlock>
                          <AuthorAvatar>
                            {msg.author?.charAt(0)?.toUpperCase() || "A"}
                          </AuthorAvatar>
                          <AuthorName theme={theme}>{msg.author}</AuthorName>
                        </AuthorBlock>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <MessageTime theme={theme}>{msg.date || msg.timestamp}</MessageTime>
                          <BookmarkIconBtn
                            type="button"
                            theme={theme}
                            $isSaved={isMessageSaved(msg.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSaveMessage(msg);
                            }}
                            title={isMessageSaved(msg.id) ? "Saved" : "Save"}
                          >
                            {isMessageSaved(msg.id) ? <FaBookmark /> : <FiBookmark />}
                          </BookmarkIconBtn>
                        </div>
                      </MessageHeader>

                      {msg.text && <MessageText theme={theme}>{msg.text}</MessageText>}

                      {/* Attached Media (Screenshots, Charts, Video) */}
                      {msg.media_info && (
                        <MediaContainer theme={theme}>
                          {msg.media_info.type === 'image' && msg.media_info.url && (
                            <ImagePreview
                              src={msg.media_info.url.startsWith('http') ? msg.media_info.url : `${API_URL}${msg.media_info.url}`}
                              alt="Chart setup"
                              onLoad={() => {
                                if (isAtBottomRef.current && messageListRef.current) {
                                  messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
                                }
                              }}
                              onClick={() => setLightboxImage(msg.media_info.url.startsWith('http') ? msg.media_info.url : `${API_URL}${msg.media_info.url}`)}
                              title="Click to expand chart"
                            />
                          )}
                          {msg.media_info.type === 'video' && (
                            msg.media_info.has_stream ? (
                              <VideoPlayer
                                controls
                                preload="metadata"
                                poster={msg.media_info.thumb ? `${API_URL}${msg.media_info.thumb}` : undefined}
                              >
                                <source src={`${API_URL}${msg.media_info.url}`} type="video/mp4" />
                                Your browser does not support the video tag.
                              </VideoPlayer>
                            ) : (
                              msg.media_info.thumb && (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  <ImagePreview
                                    src={`${API_URL}${msg.media_info.thumb}`}
                                    alt="Video preview"
                                    onLoad={() => {
                                      if (isAtBottomRef.current && messageListRef.current) {
                                        messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
                                      }
                                    }}
                                    onClick={() => setLightboxImage(`${API_URL}${msg.media_info.thumb}`)}
                                  />
                                  <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>
                                    ▶ Video Clip
                                  </div>
                                </div>
                              )
                            )
                          )}
                        </MediaContainer>
                      )}

                      {/* Detected Trade Signal Box */}
                      {hasSignal && (
                        <SignalBox $direction={msg.signal.direction}>
                          <SignalLeft>
                            <SignalBadge $direction={msg.signal.direction}>
                              {msg.signal.direction === 'LONG' ? <FaArrowUp /> : <FaArrowDown />}
                              {msg.signal.direction}
                            </SignalBadge>
                            <strong style={{ fontSize: '13px' }}>{msg.signal.asset}</strong>
                          </SignalLeft>

                          <SignalLevels>
                            {msg.signal.entry && (
                              <span>Entry: <LevelSpan theme={theme}>{msg.signal.entry}</LevelSpan></span>
                            )}
                            {msg.signal.stop_loss && (
                              <span>SL: <LevelSpan theme={theme} $color="#ef4444">{msg.signal.stop_loss}</LevelSpan></span>
                            )}
                            {msg.signal.take_profit && (
                              <span>TP: <LevelSpan theme={theme} $color="#10b981">{msg.signal.take_profit}</LevelSpan></span>
                            )}

                            <IconBtn
                              theme={theme}
                              style={{ width: '26px', height: '26px', fontSize: '11px' }}
                              onClick={() => handleCopySignal(msg.signal)}
                              title="Copy trade signal"
                            >
                              <FaCopy />
                            </IconBtn>
                          </SignalLevels>
                        </SignalBox>
                      )}
                    </MessageCard>
                  </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </MessageList>

            {/* Floating Day Navigator Widget */}
            <FloatingDayNav theme={theme}>
              <FloatingNavBtn
                type="button"
                theme={theme}
                onClick={handleNavUp}
              >
                <FaArrowUp />
              </FloatingNavBtn>
              <FloatingNavBtn
                type="button"
                theme={theme}
                onClick={handleNavDown}
              >
                <FaArrowDown />
              </FloatingNavBtn>
            </FloatingDayNav>

          </>
        ) : (
            <div style={{ textAlign: 'center', padding: '100px 20px', opacity: 0.6 }}>
              <h3>No channel selected</h3>
              <p>Select a channel on the left or click the "+" button to follow one.</p>
            </div>
          )}
        </PaneContainer>
      </DiscussionMain>

      {/* --- 3. MODAL: ADD CHANNEL (+) --- */}
      {addModalOpen && (
        <ModalOverlay onClick={() => setAddModalOpen(false)}>
          <ModalBox theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle theme={theme} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaPlus style={{ color: '#82aaff' }} /> Follow
              </ModalTitle>
              <IconBtn theme={theme} onClick={() => setAddModalOpen(false)}>✕</IconBtn>
            </ModalHeader>

            <form onSubmit={handleAddChannel} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <FormGroup>
                <FormLabel theme={theme}>Platform *</FormLabel>
                <FormSelect
                  theme={theme}
                  value={channelForm.platform}
                  onChange={(e) => {
                    const nextPlat = e.target.value;
                    setChannelForm({ ...channelForm, platform: nextPlat });
                    if (nextPlat === "discord" && discordGuilds.length === 0) {
                      fetchDiscordGuilds();
                    }
                  }}
                >
                  <option value="telegram">Telegram</option>
                  <option value="discord">Discord</option>
                </FormSelect>
              </FormGroup>

              {/* Discord 3-Dropdown Selectors - integrated directly in the same form */}
              {channelForm.platform === "discord" && (() => {
                const srvObj = discordGuilds.find(g => g.id === selectedGuild);
                const srvNameLower = (srvObj?.name || "").toLowerCase();
                const isXeilos = srvNameLower.includes("xeilos") || String(selectedGuild) === "1227741645462831215";
                const isBennett = srvNameLower.includes("bennett") || String(selectedGuild) === "923785597552848917";

                const discordFilteredChannels = guildChannels.filter(sc => {
                  if (isXeilos) {
                    return (sc.category || "").toLowerCase().includes("swing");
                  }
                  if (isBennett) {
                    const cat = (sc.category || "").toLowerCase();
                    return cat.includes("trader") && !cat.includes("only");
                  }
                  return true;
                });

                const availableCategories = Array.from(
                  new Set(discordFilteredChannels.map(sc => sc.category || "General"))
                );

                const activeCategory = selectedCategory || (availableCategories.length === 1 ? availableCategories[0] : "");

                const categoryChannels = discordFilteredChannels.filter(
                  sc => (sc.category || "General") === activeCategory
                );

                return (
                  <>
                    {/* 1. Discord Server with inline refresh button */}
                    <FormGroup>
                      <FormLabel theme={theme}>
                        <FaDiscord style={{ color: '#5865F2', marginRight: '6px', verticalAlign: 'middle' }} />
                        Server *
                      </FormLabel>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <FormSelect
                          theme={theme}
                          style={{ flex: 1 }}
                          value={selectedGuild}
                          onChange={(e) => {
                            const gid = e.target.value;
                            setSelectedGuild(gid);
                            fetchGuildChannels(gid);
                          }}
                        >
                          <option value="">-- Select a Server --</option>
                          {discordGuilds.map(g => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </FormSelect>
                        <RefreshSpinBtn
                          type="button"
                          theme={theme}
                          disabled={refreshingGuilds}
                          className={refreshingGuilds ? "spinning" : ""}
                          onClick={() => fetchDiscordGuilds()}
                          title="Refresh Discord Servers"
                        >
                          <FiRefreshCw />
                        </RefreshSpinBtn>
                      </div>
                    </FormGroup>

                    {/* 2. Category */}
                    <FormGroup>
                      <FormLabel theme={theme}>Category *</FormLabel>
                      <FormSelect
                        theme={theme}
                        disabled={!selectedGuild || loadingGuildChannels}
                        value={activeCategory}
                        onChange={(e) => {
                          setSelectedCategory(e.target.value);
                          setSelectedDiscordChannel("");
                        }}
                      >
                        {!selectedGuild ? (
                          <option value="">-- Select a Server first --</option>
                        ) : loadingGuildChannels ? (
                          <option value="">-- Loading categories... --</option>
                        ) : availableCategories.length === 0 ? (
                          <option value="">-- No categories available --</option>
                        ) : (
                          <>
                            <option value="">-- Select a Category --</option>
                            {availableCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </>
                        )}
                      </FormSelect>
                    </FormGroup>

                    {/* 3. Channel */}
                    <FormGroup>
                      <FormLabel theme={theme}>Channel (#) *</FormLabel>
                      <FormSelect
                        theme={theme}
                        disabled={!activeCategory || categoryChannels.length === 0}
                        value={selectedDiscordChannel}
                        onChange={(e) => {
                          const chId = e.target.value;
                          setSelectedDiscordChannel(chId);
                          const pickedCh = categoryChannels.find(c => c.id === chId);
                          if (pickedCh) {
                            const srvName = srvObj?.name || "Discord";
                            const cleanChName = (pickedCh.name || '').replace(/^[#\s]+/, '');
                            const catName = activeCategory || pickedCh.category || "General";
                            setChannelForm({
                              ...channelForm,
                              name: cleanChName,
                              author: srvName,
                              server: srvName,
                              discord_category: catName,
                              handle: pickedCh.id,
                              channel_id: pickedCh.id,
                              category: channelForm.category || "Technical",
                              url: `https://discord.com/channels/${selectedGuild}/${pickedCh.id}`,
                              description: `Channel #${cleanChName} on ${srvName}`
                            });
                          }
                        }}
                      >
                        {!activeCategory ? (
                          <option value="">-- Select a Category first --</option>
                        ) : categoryChannels.length === 0 ? (
                          <option value="">-- No channels found --</option>
                        ) : (
                          <>
                            <option value="">-- Select a Channel (#) --</option>
                            {categoryChannels.map(ch => (
                              <option key={ch.id} value={ch.id}>
                                #{ch.name.replace(/^[#\s]+/, '')}
                              </option>
                            ))}
                          </>
                        )}
                      </FormSelect>
                    </FormGroup>
                  </>
                );
              })()}

              {/* Telegram Link (only for Telegram) */}
              {channelForm.platform === "telegram" && (
                <FormGroup>
                  <FormLabel theme={theme}>Link *</FormLabel>
                  <FormInput
                    theme={theme}
                    required
                    placeholder="@TheMacroCompass or https://t.me/..."
                    value={channelForm.handle}
                    onChange={(e) => setChannelForm({ ...channelForm, handle: e.target.value })}
                  />
                </FormGroup>
              )}

              {/* Display Name: pre-filled with channel name by default, freely editable */}
              <FormGroup>
                <FormLabel theme={theme}>Display Name *</FormLabel>
                <FormInput
                  theme={theme}
                  required
                  placeholder={channelForm.platform === "discord" ? "e.g. idée-de-trade or Xeilos Alpha" : "e.g. ICT Alpha Room"}
                  value={channelForm.name}
                  onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel theme={theme}>Specialty</FormLabel>
                <FormSelect
                  theme={theme}
                  value={channelForm.category}
                  onChange={(e) => setChannelForm({ ...channelForm, category: e.target.value })}
                >
                  <option value="Fundamentals">Fundamentals</option>
                  <option value="Technical">Technical</option>
                  <option value="Hybrid">Hybrid</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel theme={theme}>Notes / Description</FormLabel>
                <FormInput
                  theme={theme}
                  placeholder="Trading style, London/NY session focus, etc."
                  value={channelForm.description}
                  onChange={(e) => setChannelForm({ ...channelForm, description: e.target.value })}
                />
              </FormGroup>

              <ModalActions>
                <IconBtn type="button" theme={theme} onClick={() => setAddModalOpen(false)} style={{ width: 'auto', padding: '0 14px' }}>
                  Cancel
                </IconBtn>
                <SubmitBtn type="submit" title="Follow" style={{ padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaPlus />
                </SubmitBtn>
              </ModalActions>
            </form>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* --- 4. MODAL: CONNECTION & API KEYS CONFIG --- */}
      {configModalOpen && (
        <ModalOverlay onClick={() => setConfigModalOpen(false)}>
          <ModalBox theme={theme} $width="520px" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle theme={theme}>
                <FiKey style={{ color: '#82aaff' }} /> Telegram & Discord Credentials
              </ModalTitle>
              <IconBtn theme={theme} onClick={() => setConfigModalOpen(false)}>✕</IconBtn>
            </ModalHeader>

            <form onSubmit={handleSaveConfig} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Telegram Status */}
              <div style={{ padding: '12px', borderRadius: '10px', background: 'rgba(34, 158, 217, 0.1)', border: '1px solid rgba(34, 158, 217, 0.25)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <strong style={{ color: '#229ED9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaTelegramPlane /> Telegram MTProto Connection
                  </strong>
                  <LiveStatusBadge style={{
                    background: configStatus.telegram_connected ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    color: configStatus.telegram_connected ? '#10b981' : '#ef4444'
                  }}>
                    {configStatus.telegram_connected ? "● Connected (Active Session)" : "● Not Connected"}
                  </LiveStatusBadge>
                </div>
                <p style={{ fontSize: '11.5px', margin: 0, opacity: 0.8 }}>
                  Your live session is authenticated with Telegram API ID {configStatus.telegram_api_id || "configured in .env"}.
                </p>
              </div>

              {/* Discord Connection Input */}
              <div style={{ padding: '12px', borderRadius: '10px', background: 'rgba(88, 101, 242, 0.1)', border: '1px solid rgba(88, 101, 242, 0.25)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong style={{ color: '#5865F2', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaDiscord /> Discord Authentication
                  </strong>
                  <LiveStatusBadge style={{
                    background: configStatus.discord_connected ? 'rgba(16, 185, 129, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                    color: configStatus.discord_connected ? '#10b981' : '#eab308'
                  }}>
                    {configStatus.discord_connected ? `● Connected (${configStatus.discord_type || "Active"})` : "● Enter Token"}
                  </LiveStatusBadge>
                </div>

                {configStatus.discord_masked && (
                  <div style={{ fontSize: '11px', marginBottom: '8px', opacity: 0.75, fontFamily: 'monospace' }}>
                    Active Token: {configStatus.discord_masked}
                  </div>
                )}

                {/* Generator Script Callout */}
                <div style={{ padding: '10px 12px', borderRadius: '8px', background: 'rgba(0, 0, 0, 0.25)', border: '1px solid rgba(88, 101, 242, 0.2)', marginBottom: '10px', fontSize: '11.5px', lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 700, color: '#82aaff', marginBottom: '4px' }}>⚡ Auto-Generate Token via Script</div>
                  <div>In terminal, run: <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px', color: '#10b981' }}>python backend/workflow/collector/tasks/fundas/src/generate_discord_token.py</code></div>
                  <div style={{ opacity: 0.8, fontSize: '10.5px', marginTop: '2px' }}>Enter your Discord email & password to automatically generate and save your secret token.</div>
                </div>

                <FormGroup>
                  <FormLabel theme={theme}>Or enter Discord User / Bot Token manually</FormLabel>
                  <FormInput
                    theme={theme}
                    type="password"
                    placeholder="e.g. MTIzNDU2Nzg5... (User Auth Token or Bot Token)"
                    value={configForm.discord_token}
                    onChange={(e) => setConfigForm({ ...configForm, discord_token: e.target.value })}
                  />
                  <span style={{ fontSize: '10.5px', opacity: 0.6 }}>
                    Allows Trade Terminal to read messages from Discord guilds and private trader channels.
                  </span>
                </FormGroup>
              </div>

              <ModalActions>
                <IconBtn type="button" theme={theme} onClick={() => setConfigModalOpen(false)} style={{ width: 'auto', padding: '0 14px' }}>
                  Close
                </IconBtn>
                <SubmitBtn type="submit">
                  <FaCheck /> Save Credentials
                </SubmitBtn>
              </ModalActions>
            </form>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* --- 5. MODAL: IMAGE / CHART LIGHTBOX --- */}
      {lightboxImage && (
        <ModalOverlay onClick={() => setLightboxImage(null)} style={{ zIndex: 3000, background: 'rgba(0, 0, 0, 0.88)' }}>
          <div style={{ position: 'relative', maxWidth: '92vw', maxHeight: '92vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxImage}
              alt="Expanded Chart Setup"
              style={{
                maxWidth: '92vw',
                maxHeight: '92vh',
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9)'
              }}
            />
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ef4444',
                color: '#fff',
                border: '2px solid #fff',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: '14px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
              }}
              title="Close Fullscreen View"
            >
              ✕
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* --- 6. MODAL: CHANNEL DETAILS & EDIT --- */}
      {channelDetailsModal && (
        <ModalOverlay onClick={() => { setChannelDetailsModal(null); setIsEditingChannel(false); }}>
          <ModalBox theme={theme} $width="480px" onClick={(e) => e.stopPropagation()}>
            <ModalHeader style={{
              justifyContent: isEditingChannel ? 'space-between' : 'flex-end',
              borderBottom: isEditingChannel ? undefined : 'none',
              paddingBottom: isEditingChannel ? undefined : 0
            }}>
              {isEditingChannel && (
                <ModalTitle theme={theme}>
                  <FiEdit2 style={{ color: '#82aaff' }} /> Edit Channel
                </ModalTitle>
              )}
              <IconBtn theme={theme} onClick={() => { setChannelDetailsModal(null); setIsEditingChannel(false); }}>✕</IconBtn>
            </ModalHeader>

            {isEditingChannel ? (
              <form onSubmit={handleSaveEdit} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
                <FormGroup>
                  <FormLabel theme={theme}>Display Name *</FormLabel>
                  <FormInput
                    theme={theme}
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="e.g. idée-de-trade or Xeilos Alpha"
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel theme={theme}>Specialty</FormLabel>
                  <FormSelect
                    theme={theme}
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  >
                    <option value="Fundamentals">Fundamentals</option>
                    <option value="Technical">Technical</option>
                    <option value="Hybrid">Hybrid</option>
                    {editForm.category && !["Fundamentals", "Technical", "Hybrid"].includes(editForm.category) && (
                      <option value={editForm.category}>{editForm.category}</option>
                    )}
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel theme={theme}>Notes</FormLabel>
                  <FormInput
                    theme={theme}
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Trading style, London/NY session focus, etc."
                  />
                </FormGroup>

                <ModalActions style={{ marginTop: '8px', display: 'flex', gap: '10px' }}>
                  <SubmitBtn type="submit" disabled={savingEdit} style={{ flex: 1, height: '38px', borderRadius: '8px' }}>
                    {savingEdit ? "Saving..." : "Save"}
                  </SubmitBtn>
                  <IconBtn
                    type="button"
                    theme={theme}
                    onClick={() => setIsEditingChannel(false)}
                    style={{ flex: 1, height: '38px', borderRadius: '8px', fontWeight: 700 }}
                  >
                    Cancel
                  </IconBtn>
                </ModalActions>
              </form>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{
                  padding: '14px 18px',
                  borderRadius: '12px',
                  background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
                }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: 800 }}>{channelDetailsModal.name}</h4>
                  <span style={{ fontSize: '12px', color: channelDetailsModal.platform === 'telegram' ? '#229ED9' : '#5865F2', fontWeight: 700 }}>
                    {channelDetailsModal.handle || channelDetailsModal.channel_id}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {/* 1. PLATFORM */}
                  <div style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                    <div style={{ fontSize: '10px', opacity: 0.6, marginBottom: '4px', fontWeight: 800, letterSpacing: '0.5px' }}>PLATFORM</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {channelDetailsModal.platform === 'telegram' ? <FaTelegramPlane style={{ color: '#229ED9' }} /> : <FaDiscord style={{ color: '#5865F2' }} />}
                      {channelDetailsModal.platform === 'telegram' ? 'Telegram' : 'Discord'}
                    </div>
                  </div>

                  {channelDetailsModal.platform === 'discord' ? (
                    <>
                      {/* 2. SERVER */}
                      <div style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                        <div style={{ fontSize: '10px', opacity: 0.6, marginBottom: '4px', fontWeight: 800, letterSpacing: '0.5px' }}>SERVER</div>
                        <div style={{ fontSize: '13px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {channelDetailsModal.server || channelDetailsModal.author || "Discord"}
                        </div>
                      </div>

                      {/* 3. CATEGORY */}
                      <div style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                        <div style={{ fontSize: '10px', opacity: 0.6, marginBottom: '4px', fontWeight: 800, letterSpacing: '0.5px' }}>CATEGORY</div>
                        <div style={{ fontSize: '13px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {channelDetailsModal.discord_category || channelDetailsModal.server_category || (() => {
                            const nameLower = (channelDetailsModal.name || '').toLowerCase();
                            const authorLower = (channelDetailsModal.author || '').toLowerCase();
                            if (nameLower.includes('idée') || authorLower.includes('xeilos')) return '⏳📊 Swing';
                            if (nameLower.includes('trader') || authorLower.includes('bennett')) return '🟡⚫ TRADER 🟡⚫';
                            return 'General';
                          })()}
                        </div>
                      </div>

                      {/* 4. SPECIALTY */}
                      <div style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                        <div style={{ fontSize: '10px', opacity: 0.6, marginBottom: '4px', fontWeight: 800, letterSpacing: '0.5px' }}>SPECIALTY</div>
                        <div style={{ fontSize: '13px', fontWeight: 700 }}>{channelDetailsModal.category || "Fundamentals"}</div>
                      </div>
                    </>
                  ) : (
                    /* For Telegram */
                    <div style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                      <div style={{ fontSize: '10px', opacity: 0.6, marginBottom: '4px', fontWeight: 800, letterSpacing: '0.5px' }}>SPECIALTY</div>
                      <div style={{ fontSize: '13px', fontWeight: 700 }}>{channelDetailsModal.category || "Fundamentals"}</div>
                    </div>
                  )}
                </div>

                {channelDetailsModal.description && (
                  <div style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                    <div style={{ fontSize: '10px', opacity: 0.6, marginBottom: '4px', fontWeight: 800, letterSpacing: '0.5px' }}>NOTES</div>
                    <div style={{ fontSize: '12.5px', lineHeight: '1.5' }}>{channelDetailsModal.description}</div>
                  </div>
                )}

                {channelDetailsModal.url && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: '8px', background: 'rgba(130, 170, 255, 0.08)', border: '1px solid rgba(130, 170, 255, 0.2)' }}>
                    <div style={{ minWidth: 0, flex: 1, marginRight: '10px' }}>
                      <div style={{ fontSize: '10px', opacity: 0.7, fontWeight: 800, letterSpacing: '0.5px' }}>LINK</div>
                      <div style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {channelDetailsModal.url}
                      </div>
                    </div>
                    <IconBtn
                      as="a"
                      href={channelDetailsModal.url}
                      target="_blank"
                      rel="noreferrer"
                      theme={theme}
                      title="Open channel in app / web"
                      style={{ width: '30px', height: '30px', flexShrink: 0 }}
                    >
                      <FiExternalLink />
                    </IconBtn>
                  </div>
                )}

                <ModalActions style={{ marginTop: '8px', display: 'flex', gap: '10px' }}>
                  <IconBtn
                    type="button"
                    theme={theme}
                    onClick={handleStartEdit}
                    style={{ flex: 1, height: '38px', borderRadius: '8px', fontWeight: 700, gap: '6px' }}
                  >
                    <FiEdit2 /> Edit
                  </IconBtn>
                  <IconBtn
                    type="button"
                    theme={theme}
                    onClick={() => { setChannelDetailsModal(null); setIsEditingChannel(false); }}
                    style={{ flex: 1, height: '38px', borderRadius: '8px', fontWeight: 700 }}
                  >
                    Close
                  </IconBtn>
                </ModalActions>
              </div>
            )}
          </ModalBox>
        </ModalOverlay>
      )}

      {/* --- 7. MODAL: DELETE CONFIRMATION --- */}
      {channelToDelete && (
        <ModalOverlay onClick={() => !deletingChannel && setChannelToDelete(null)}>
          <ModalBox theme={theme} $width="420px" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle theme={theme} style={{ color: '#ef4444' }}>
                <FaTrash style={{ fontSize: '15px' }} /> Remove Channel
              </ModalTitle>
              <IconBtn theme={theme} onClick={() => !deletingChannel && setChannelToDelete(null)}>✕</IconBtn>
            </ModalHeader>

            <div style={{ fontSize: '14px', lineHeight: 1.6, color: theme.palette.text.primary }}>
              Are you sure you want to remove <strong style={{ color: theme.palette.mode === 'dark' ? '#82aaff' : '#2563eb' }}>{channelToDelete.name}</strong> from your followed list?
              <div style={{ fontSize: '12.5px', marginTop: '8px', opacity: 0.7 }}>
                This channel will no longer appear in your channels feed.
              </div>
            </div>

            <ModalActions style={{ marginTop: '14px' }}>
              <IconBtn
                type="button"
                theme={theme}
                disabled={deletingChannel}
                onClick={() => setChannelToDelete(null)}
                style={{ width: 'auto', padding: '0 16px', height: '36px' }}
              >
                Cancel
              </IconBtn>
              <DangerBtn
                type="button"
                disabled={deletingChannel}
                onClick={confirmDeleteChannel}
                style={{ height: '36px' }}
              >
                <FaTrash style={{ fontSize: '12px' }} />
                {deletingChannel ? "Removing..." : "Remove"}
              </DangerBtn>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* Context Menu on Right Click */}
      {contextMenu && (
        <ContextMenuBox
          theme={theme}
          style={{
            top: Math.min(contextMenu.y, window.innerHeight - 170),
            left: Math.min(contextMenu.x, window.innerWidth - 220)
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ContextMenuItem
            theme={theme}
            onClick={() => {
              toggleSaveMessage(contextMenu.message);
              setContextMenu(null);
            }}
          >
            <FiBookmark style={{ color: isMessageSaved(contextMenu.message.id) ? '#ef4444' : '#82aaff' }} />
            {isMessageSaved(contextMenu.message.id) ? "Remove from Saved" : "Save Message"}
          </ContextMenuItem>

          {contextMenu.message.text && (
            <ContextMenuItem
              theme={theme}
              onClick={() => {
                navigator.clipboard.writeText(contextMenu.message.text);
                showToast("Message text copied!");
                setContextMenu(null);
              }}
            >
              <FaCopy style={{ opacity: 0.7 }} /> Copy Message Text
            </ContextMenuItem>
          )}

          {contextMenu.message.signal && (
            <ContextMenuItem
              theme={theme}
              onClick={() => {
                handleCopySignal(contextMenu.message.signal);
                setContextMenu(null);
              }}
            >
              <FaCheck style={{ color: '#10b981' }} /> Copy Trade Signal
            </ContextMenuItem>
          )}
        </ContextMenuBox>
      )}

      {/* Floating Toast Notification */}
      {saveToast && (
        <ToastBox theme={theme}>
          <FiBookmark style={{ color: '#82aaff' }} />
          <span>{saveToast}</span>
        </ToastBox>
      )}
    </Workspace>
  );
}
